import { Component, OnDestroy, OnInit, effect, inject, signal, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { finalize, Subject, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem, SortEvent } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { DynamicFilterConfig, DynamicFilterValues, createDynamicFilterState } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { IMEXAdminGroup, IMEXViewerGroup } from '../../../models/roles/roles-group';
import { Receipt, ReceiptSearchCommand } from '../../../models/receipt/receipt';
import { ReceiptType } from '../../../models/receipt/receipt-type-enum';
import { ReceiptProxyService } from '../../../proxies/order/receipt.proxy.service';
import { EnumService } from '../../../services/enum.service';
import { EnumValues } from '@card-software/library';
import { setOptionsForPrimeNg } from '../../../PrimeNG/utils';

interface ReceiptTypeOption {
  label: string;
  value: ReceiptType;
}

@Component({
  selector: 'app-receipts',
  templateUrl: './receipts.component.html',
  styleUrl: './receipts.component.scss',
})
export class ReceiptsComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private translate = inject(TranslateService);
  private receiptProxyService = inject(ReceiptProxyService);
  private enumService = inject(EnumService);

  isLoading = signal(0);
  listReceipts = signal<Receipt[]>([]);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({ storageKey: 'receipt-filters', config: this.filterConfig });
  showFilters = signal(false);
  currentPage = signal<number>(1);
  totalRecords = signal<number>(0);
  pageSize = signal<number>(25);
  sortField = signal<string | undefined>(undefined);
  sortOrder = signal<number>(-1);

  statusEnum: EnumValues = {};

  private readonly typeOptions: ReceiptTypeOption[] = [
    { label: 'Fiscal', value: ReceiptType.Fiscal },
    { label: 'Purchase', value: ReceiptType.Purchase },
  ];

  // SpeedDial actions for the "New" button — the user picks the receipt kind, which is passed to the
  // detail page in create mode via the `type` query param.
  newReceiptActions: MenuItem[] = [
    {
      label: 'Fiscal',
      icon: 'pi pi-file',
      command: () => this.newReceipt(ReceiptType.Fiscal),
    },
    {
      label: 'Purchase',
      icon: 'pi pi-shopping-cart',
      command: () => this.newReceipt(ReceiptType.Purchase),
    },
  ];

  canEdit = computed(() => {
    if (AuthService.hasRole(IMEXAdminGroup.InvoiceAdmin)) {
      return true;
    }
    if (AuthService.hasRole(IMEXViewerGroup.InvoiceViewer)) {
      return false;
    }
    return false;
  });

  private destroy$ = new Subject<void>();
  private filtersPanelInitialized = false;
  private lastSearchCriteria: ReceiptSearchCommand = {};

  getRowData(rowData: unknown): Receipt {
    return rowData as Receipt;
  }

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => {
      if (!this.filtersPanelInitialized) {
        this.filtersPanelInitialized = true;
        this.showFilters.set(this.filterState.hasActive());
      }
      this.searchWithFilters(filters);
    });

    effect(
      () => {
        this.statusEnum = this.enumService.getEnum('CustomerOrderStatus', 'orderEnums')?.values;
        if (this.translate.currentLang) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.tryInitializeFilterConfig();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  private tryInitializeFilterConfig(): void {
    if (this.translate.currentLang) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translate.instant(key);
      return translation === key ? fallback : translation;
    };

    const statusOptions = setOptionsForPrimeNg(this.statusEnum ?? {});

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'number',
          label: translate('Receipts_Number', 'Receipt Number'),
          placeholder: translate('Enter_receipt_number', 'Enter receipt number'),
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'providerInvoiceNumber',
          label: translate('Receipts_Provider_Invoice_Number', 'Provider Invoice Number'),
          placeholder: translate('Enter_provider_invoice_number', 'Enter provider invoice number'),
          defaultVisible: true,
        },
        {
          type: 'multiselect',
          formControlName: 'statuses',
          label: translate('Receipts_Status', 'Status'),
          placeholder: translate('Select_status', 'Select status'),
          options: statusOptions,
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'type',
          label: translate('Receipts_Type', 'Type'),
          placeholder: translate('Select_type', 'Select type'),
          options: this.typeOptions,
          showClear: true,
          defaultVisible: false,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  private onSearch(searchCriteria: ReceiptSearchCommand): void {
    this.startLoading();
    this.lastSearchCriteria = searchCriteria;
    this.receiptProxyService
      .searchReceipt(searchCriteria, this.currentPage(), this.pageSize(), false, this.sortField(), this.sortOrder())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          const pagedResult = response.data;
          this.listReceipts.set(pagedResult?.items ?? []);
          this.totalRecords.set(pagedResult?.totalCount ?? 0);
        },
        error: (e) => {
          console.error(e);
          this.listReceipts.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page ?? 0) + 1);
    this.pageSize.set(event.rows ?? this.pageSize());
    this.onSearch(this.lastSearchCriteria);
  }

  onSort(event: SortEvent): void {
    const newField = this.normalizeSortField(event.field, this.sortField());
    const newOrder = event.order ?? -1;

    if (newField === this.sortField() && newOrder === this.sortOrder()) {
      return;
    }

    this.sortField.set(newField);
    this.sortOrder.set(newOrder);
    this.currentPage.set(1);
    this.onSearch(this.lastSearchCriteria);
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    const typeRaw = filters['type'];
    const typeValue = typeRaw === null || typeRaw === undefined || typeRaw === '' ? undefined : (Number(typeRaw) as ReceiptType);

    const searchCriteria: ReceiptSearchCommand = {
      number: (filters['number'] as string)?.trim() || undefined,
      providerInvoiceNumber: (filters['providerInvoiceNumber'] as string)?.trim() || undefined,
      type: typeValue,
      statuses:
        Array.isArray(filters['statuses']) && (filters['statuses'] as unknown[]).length > 0
          ? (filters['statuses'] as unknown[]).map((item: unknown) =>
              typeof item === 'object' && item !== null ? (item as Record<string, number>)['value'] : (item as number),
            )
          : undefined,
    };

    this.currentPage.set(1);
    this.onSearch(searchCriteria);
  }

  public redirectToDetails(receiptId: string): void {
    this.router.navigate(['/receipt-detail'], { queryParams: { id: receiptId } });
  }

  public newReceipt(type: ReceiptType): void {
    this.router.navigate(['/receipt-detail'], { queryParams: { id: 'new', type } });
  }

  typeLabel(type: ReceiptType): string {
    return type === ReceiptType.Purchase ? 'Purchase' : 'Fiscal';
  }

  statusLabel(status: number): string {
    return this.statusEnum?.[status] ?? '';
  }

  private normalizeSortField(sortField: string | string[] | undefined | null, fallback: string | undefined): string | undefined {
    if (Array.isArray(sortField)) {
      return sortField[0] ?? fallback;
    }
    return sortField ?? fallback;
  }
}
